import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SmsIcon from '@mui/icons-material/SmsOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined';
import ScheduleSend from '@mui/icons-material/ScheduleSendOutlined';
import Send from '@mui/icons-material/SendOutlined';
import { ServiceCategory } from '@prisma/client';
import { toast } from 'react-toastify';
import { CircularProgress, Stack, Typography } from '@mui/material';
import StepsFlow, {
  Step,
  StepFormData,
  StepInput,
} from '../../global/StepsFlow';
import FullPageModal from '../../modal/FullPageModal';
import StepFlowImage from '../../global/StepsFlow/StepFlowImage';
import CampaignTemplateDesigner from '../CampaignTemplateDesigner';
import useGetMarketingData from '../../../hooks/marketing/useGetMarketingData';
import useGetEmailTemplates from '../../../hooks/marketing/useGetEmailTemplates';
import useGetSmsTemplates from '../../../hooks/marketing/useGetSmsTemplates';
import useCreateAutomation from '../../../hooks/marketing/useCreateAutomation';
import useLoadingModal from '../../../hooks/global/useLoadingModal';
import {
  TEMPLATE_PLACEHOLDER_URL,
  MarketingTab,
} from '../../../constants/marketing';
import { AUTOMATION_SERVICE_CATEGORY_LABEL } from '../../../constants/automations';
import {
  getEmailTemplatePreviewUrl,
  getSmsTemplatePreviewUrl,
  isEmailTemplateValid,
} from '../../../helpers/marketing';
import { buildSendFromStep } from '../../../helpers/marketing/sendFromStep';
import useGetEmailSenders from '../../../hooks/marketing/useGetEmailSenders';
import useUser from '../../../hooks/global/useUser';
import { ROUTES } from '../../../constants/global';
import {
  describeTrigger,
  getDefaultEmailAddressId,
  getDelaySeconds,
  getEmptyAutomationFormValues,
  recipeToFormValues,
} from '../../../helpers/marketing/automations';
import {
  AutomationFormValues,
  AutomationRecipe,
  getAutomationFormSchema,
} from '../../../types/marketing/automations';
import {
  MarketingEmailTemplateWithSource,
  MarketingSmsTemplateWithSource,
} from '../../../types/marketing';
import { TemplateEmbedData } from '../CampaignTemplateDesigner/TemplateEmbed';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  recipe: AutomationRecipe | null;
  /// Resolved before the modal opens, so a recipe lands on a ready template
  template: TemplateEmbedData | null;
}

type SetValues = Dispatch<SetStateAction<AutomationFormValues>>;

const EMPTY_TEMPLATES: MarketingEmailTemplateWithSource[] = [];
const EMPTY_SMS_TEMPLATES: MarketingSmsTemplateWithSource[] = [];

function AutomationEditorModal({
  isOpen,
  onClose,
  recipe,
  template: recipeTemplate,
}: Props) {
  const {
    data: marketingData,
    isLoading: isLoadingMarketingData,
    isError: isMarketingDataError,
  } = useGetMarketingData();
  const isReady = !!marketingData?.id;

  const { data: emailTemplatesData } = useGetEmailTemplates({
    enabled: isOpen,
  });
  const emailTemplates = emailTemplatesData?.templates ?? EMPTY_TEMPLATES;
  const { data: smsTemplatesData } = useGetSmsTemplates({ enabled: isOpen });
  const { emailAddresses } = useGetEmailSenders({ enabled: isOpen });
  const { user } = useUser();
  const navigate = useNavigate();
  const smsTemplates = smsTemplatesData?.templates ?? EMPTY_SMS_TEMPLATES;

  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const [isTemplateDesignerOpen, setIsTemplateDesignerOpen] =
    React.useState(false);
  const [currentValues, setCurrentValues] =
    React.useState<AutomationFormValues | null>(null);
  const setValuesRef = React.useRef<SetValues | null>(null);

  const { mutateAsync: createAutomation, isPending: isCreating } =
    useCreateAutomation({
      onMutate: () => {
        openLoadingModal({
          header: 'Creating automation',
          message: 'Please wait while the automation is being turned on',
        });
      },
      onSettled: () => closeLoadingModal(),
      onSuccess: () => {
        toast.success('Automation is live');
        onClose();
      },
      onError: message => {
        toast.error(message);
      },
    });

  const getTemplatePreview = useCallback(
    (values: AutomationFormValues) => {
      if (values.templateId === null) return null;
      if (values.channel === 'EMAIL') {
        const template = emailTemplates.find(t => t.id === values.templateId);
        if (!template) return null;
        return {
          template,
          name: template.source?.name || 'Unnamed Template',
          subject: template.source?.subject || '',
          previewUrl: getEmailTemplatePreviewUrl(template),
          isValid: isEmailTemplateValid(template.source),
        };
      }
      const template = smsTemplates.find(t => t.id === values.templateId);
      if (!template) return null;
      return {
        template,
        name: template.source?.name || 'Unnamed Template',
        subject: '',
        previewUrl: getSmsTemplatePreviewUrl(template),
        isValid: true,
      };
    },
    [emailTemplates, smsTemplates],
  );

  const recipeChannel = recipe?.channel;
  const recipeTemplateId = recipeTemplate?.id ?? null;
  const channel = currentValues?.channel ?? recipeChannel;

  // A template belongs to one channel, so switching channels drops it (or restores the recipe's)
  React.useEffect(() => {
    if (!channel) return;
    setValuesRef.current?.(prev => {
      const templateId = channel === recipeChannel ? recipeTemplateId : null;
      return prev.templateId === templateId ? prev : { ...prev, templateId };
    });
  }, [channel, recipeChannel, recipeTemplateId]);

  const form = useMemo<StepFormData<AutomationFormValues>>(() => {
    const initialValues = {
      ...(recipe
        ? { ...recipeToFormValues(recipe), templateId: recipeTemplateId }
        : getEmptyAutomationFormValues()),
      emailAddressId: getDefaultEmailAddressId(emailAddresses),
    };

    return {
      initialValues,
      validationSchema: getAutomationFormSchema(
        emailTemplates,
        recipeTemplateId,
      ),
      onSubmit: async values => {
        await createAutomation(values).catch(() => undefined);
      },
      steps: values => {
        const isRecipe = !!recipe;
        const channelText = values.channel === 'SMS' ? 'SMS' : 'email';
        const preview = getTemplatePreview(values);

        const delayInputs: StepInput<AutomationFormValues>[] =
          values.delay.timing === 'AFTER'
            ? [
                {
                  contentType: 'text',
                  type: 'number',
                  name: 'delay.amount',
                  label: 'Wait',
                  min: 1,
                },
                {
                  contentType: 'select',
                  name: 'delay.unit',
                  label: 'Unit',
                  options: [
                    { value: 'MINUTES', label: 'Minutes' },
                    { value: 'HOURS', label: 'Hours' },
                    { value: 'DAYS', label: 'Days' },
                    { value: 'WEEKS', label: 'Weeks' },
                  ],
                },
              ]
            : [];

        const contentStep: Step<AutomationFormValues> = {
          title: 'Content',
          description: preview
            ? `Template: ${preview.name}${
                preview.subject ? `\nSubject: ${preview.subject}` : ''
              }`
            : `Design the content for your ${channelText}`,
          selectButtonText: 'Edit Content',
          isDefaultVisited: !!recipe,
          inputs: [{ contentType: 'invisible', name: 'templateId' }],
          onNavigate: () => setIsTemplateDesignerOpen(true),
          isError: preview ? !preview.isValid : false,
        };

        return [
          {
            title: 'Automation overview',
            description: values.name
              ? `${values.name} - ${
                  values.channel === 'SMS'
                    ? 'SMS automation'
                    : 'Email automation'
                }`
              : 'Enter automation details',
            selectButtonText: 'Update Overview',
            buttonText: 'Continue',
            isDefaultVisited: !!recipe,
            inputs: [
              {
                name: 'name',
                contentType: 'text',
                label: 'Internal Name',
              },
              {
                contentType: 'button-select',
                name: 'channel',
                options: [
                  {
                    icon: SmsIcon,
                    title: 'SMS',
                    value: 'SMS',
                    description: 'Send this automation via SMS',
                  },
                  {
                    icon: EmailIcon,
                    title: 'Email',
                    value: 'EMAIL',
                    description: 'Send this automation via email',
                  },
                ],
              },
            ],
          },
          {
            title: 'Trigger',
            description: describeTrigger({
              triggerEvent: values.triggerEvent,
              delaySeconds: getDelaySeconds(values.delay),
              serviceCategories: values.serviceCategories,
            }),
            selectButtonText: 'Update Trigger',
            buttonText: 'Continue',
            isDefaultVisited: !!recipe,
            inputs: [
              {
                contentType: 'button-select',
                name: 'triggerEvent',
                disabled: isRecipe,
                options: [
                  {
                    icon: ShoppingCartIcon,
                    title: 'Checkout',
                    value: 'CHECKOUT_COMPLETED',
                    description: `Send this ${channelText} after a client checks out`,
                  },
                  {
                    icon: EventAvailableIcon,
                    title: 'Booking request',
                    value: 'BOOKING_REQUEST_SUBMITTED',
                    description: `Send this ${channelText} after a client submits a booking request`,
                  },
                ],
              },
              {
                contentType: 'button-select',
                name: 'delay.timing',
                horizontal: true,
                options: [
                  {
                    icon: Send,
                    title: 'Immediately',
                    value: 'IMMEDIATE',
                    description: 'Send right after the trigger',
                  },
                  {
                    icon: ScheduleSend,
                    title: 'After a delay',
                    value: 'AFTER',
                    description: 'Wait before sending',
                  },
                ],
              },
              ...delayInputs,
              {
                contentType: 'multi-checkbox',
                name: 'serviceCategories',
                label: 'Only for these appointment types (optional)',
                isOptional: true,
                options: Object.values(ServiceCategory).map(category => ({
                  value: category,
                  label: AUTOMATION_SERVICE_CATEGORY_LABEL[category],
                })),
              },
            ],
          },
          values.channel === 'EMAIL' &&
            buildSendFromStep({
              emailAddresses,
              user,
              values,
              selectedEmailAddressId: values.emailAddressId,
              isDefaultVisited: true,
              onSetUpCustomDomain: () => {
                onClose();
                navigate(
                  `${ROUTES.marketing}?tab=${MarketingTab.CUSTOM_DOMAIN}`,
                );
              },
            }),
          contentStep,
        ].filter(Boolean) as Step<AutomationFormValues>[];
      },
    };
  }, [
    recipe,
    recipeTemplateId,
    emailTemplates,
    emailAddresses,
    user,
    navigate,
    onClose,
    createAutomation,
    getTemplatePreview,
  ]);

  const rightContent = useCallback(
    (values: AutomationFormValues) => {
      const previewUrl = getTemplatePreview(values)?.previewUrl;
      return (
        <StepFlowImage
          src={previewUrl || TEMPLATE_PLACEHOLDER_URL}
          fallbackSrc={TEMPLATE_PLACEHOLDER_URL}
          keepPrevious
          isFullWidth
          imageStyle={{
            maxWidth: '80%',
            margin: '0 auto',
          }}
        />
      );
    },
    [getTemplatePreview],
  );

  const selectedTemplate = currentValues
    ? getTemplatePreview(currentValues)?.template ?? null
    : null;

  return (
    <>
      <FullPageModal
        modalOpen={isOpen}
        name={recipe?.title ?? 'New Automation'}
        setModalOpen={onClose}
        large>
        {isReady ? (
          <StepsFlow
            form={form}
            isSequential={false}
            isFirstStepRequired
            defaultIsFirstStepComplete={!!recipe}
            initialStep={recipe ? -1 : 0}
            rightContent={rightContent}
            setValuesRef={setValuesRef}
            submitButton={{ text: 'Create', disabled: isCreating }}
            onValuesChange={setCurrentValues}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{ flex: 1, minHeight: 320 }}>
            {isMarketingDataError ? (
              <Typography variant="body2" color="text.secondary">
                We couldn&apos;t load your marketing workspace. Close this and
                try again.
              </Typography>
            ) : (
              <>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  {isLoadingMarketingData
                    ? 'Setting up your marketing workspace…'
                    : 'Loading…'}
                </Typography>
              </>
            )}
          </Stack>
        )}
      </FullPageModal>
      {isTemplateDesignerOpen && (
        <CampaignTemplateDesigner
          medium={currentValues?.channel ?? recipe?.channel ?? 'SMS'}
          isOpen={isTemplateDesignerOpen}
          onClose={() => setIsTemplateDesignerOpen(false)}
          selectedTemplate={selectedTemplate}
          initialEmbed={channel === recipeChannel ? recipeTemplate : null}
          onSelectTemplate={template => {
            setValuesRef.current?.(prev => ({
              ...prev,
              templateId: template?.id ?? null,
            }));
          }}
        />
      )}
    </>
  );
}

export default AutomationEditorModal;
